/**
 * Route Validator — Phase B
 *
 * Validates route_registry records from the database before they can
 * influence runtime routing. Calculates an integrity_score; if below
 * the INTEGRITY_THRESHOLD (80%), the entire registry is rejected and
 * the app falls back to hardcoded routes.
 */
import type { RouteRecord } from "@/hooks/useRouteRegistry";
import { isValidComponentName as isValidComponent, isValidLayoutName as isValidLayout } from "@/lib/component-registry-names";

export const INTEGRITY_THRESHOLD = 0.8; // 80%

export interface RouteWarning {
  routeId: string;
  path: string;
  field: string;
  message: string;
  severity: "error" | "warning";
}

export interface ValidationResult {
  /** 0–1 score representing the proportion of valid routes */
  integrityScore: number;
  /** Whether the registry passes the threshold and can be used */
  isHealthy: boolean;
  /** Total routes evaluated */
  totalRoutes: number;
  /** Routes that passed all checks */
  validRoutes: number;
  /** Routes that had at least one error */
  invalidRoutes: number;
  /** Per-route warnings/errors */
  warnings: RouteWarning[];
  /** Validated route records (only the ones that passed) */
  validatedRecords: RouteRecord[];
  /** Timestamp of validation */
  validatedAt: number;
}

function validateSingleRoute(route: RouteRecord): RouteWarning[] {
  const warnings: RouteWarning[] = [];
  const w = (field: string, message: string, severity: "error" | "warning" = "error") =>
    warnings.push({ routeId: route.id, path: route.path, field, message, severity });

  // 1. Path must exist and start with correct chars
  if (!route.path || route.path.trim() === "") {
    w("path", "Path is empty");
  }

  // 2. Component routes must have a valid component
  if (route.route_mode === "component") {
    if (!route.component) {
      w("component", "Component route has no component assigned");
    } else if (!isValidComponent(route.component)) {
      w("component", `Component "${route.component}" is not in the approved registry`);
    }
  }

  // 3. Redirect routes must have a target
  if (route.route_mode === "redirect") {
    if (!route.redirect_to) {
      w("redirect_to", "Redirect route has no redirect target");
    }
  }

  // 4. Layout must be valid if specified
  if (route.layout && route.layout !== "—") {
    if (!isValidLayout(route.layout)) {
      w("layout", `Layout "${route.layout}" is not in the approved registry`, "warning");
    }
  }

  // 5. Domain must be a known value
  const validDomains = ["Public", "Consumer", "Trade", "Admin", "Legacy", "Standalone", "Root", "Orphan"];
  if (!validDomains.includes(route.domain)) {
    w("domain", `Unknown domain "${route.domain}"`, "warning");
  }

  // 6. Route mode must be known
  const validModes = ["component", "redirect", "alias", "placeholder"];
  if (!validModes.includes(route.route_mode)) {
    w("route_mode", `Unknown route mode "${route.route_mode}"`);
  }

  // 7. Status must be known
  const validStatuses = ["active", "disabled", "orphan"];
  if (!validStatuses.includes(route.status)) {
    w("status", `Unknown status "${route.status}"`, "warning");
  }

  return warnings;
}

/**
 * Validate an array of route records from the database.
 * Returns a ValidationResult with integrity score and warnings.
 */
export function validateRouteRegistry(routes: RouteRecord[]): ValidationResult {
  if (routes.length === 0) {
    return {
      integrityScore: 0,
      isHealthy: false,
      totalRoutes: 0,
      validRoutes: 0,
      invalidRoutes: 0,
      warnings: [],
      validatedRecords: [],
      validatedAt: Date.now(),
    };
  }

  const allWarnings: RouteWarning[] = [];
  const validRecords: RouteRecord[] = [];
  let invalidCount = 0;

  for (const route of routes) {
    const warnings = validateSingleRoute(route);
    const hasErrors = warnings.some((w) => w.severity === "error");

    allWarnings.push(...warnings);

    if (hasErrors) {
      invalidCount++;
    } else {
      validRecords.push(route);
    }
  }

  const integrityScore = validRecords.length / routes.length;

  return {
    integrityScore,
    isHealthy: integrityScore >= INTEGRITY_THRESHOLD,
    totalRoutes: routes.length,
    validRoutes: validRecords.length,
    invalidRoutes: invalidCount,
    warnings: allWarnings,
    validatedRecords: validRecords,
    validatedAt: Date.now(),
  };
}

/**
 * Given the validated registry, determine which routes should be
 * disabled at runtime (status === 'disabled' in DB but currently
 * active in hardcoded routes).
 */
export function getDisabledPaths(validatedRoutes: RouteRecord[]): Set<string> {
  const disabled = new Set<string>();
  for (const route of validatedRoutes) {
    if (route.status === "disabled") {
      disabled.add(route.path);
    }
  }
  return disabled;
}

/**
 * Given validated routes, extract redirect overrides from the DB
 * that differ from hardcoded routing.
 */
export function getRedirectOverrides(validatedRoutes: RouteRecord[]): Map<string, string> {
  const redirects = new Map<string, string>();
  for (const route of validatedRoutes) {
    if (route.route_mode === "redirect" && route.redirect_to && route.status === "active") {
      redirects.set(route.path, route.redirect_to);
    }
  }
  return redirects;
}
